import React, { useCallback, useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { Box, Paper, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import LinearProgress from '@material-ui/core/LinearProgress/LinearProgress';
import {
  WildCardDomainsResourceInfoLoadingSelector as resourceLoadingSelector,
  WildCardDomainsResourceInfoSelector as resourceSelector,
  WildCardDomainsValidationResultSelector as validationResultSelector,
  WildCardDomainsValidationResultLoadingSelector as validationLoadingSelector,
  WildCardDomainsAssignDomainLoading as assignDomainLoadingSelector,
  WildCardDomainsAssignDomainResult as assignResultSelector,
  WildCardDomainsCustomDomain as customDomainSelector,
} from '@store/slices/wildcardDomains';
import {
  ThunkAssignDomain,
  ThunkGetAzureResourceData,
  ThunkGetCustomDomain,
  ThunkValidateDomain,
} from '@store/slices/wildcardDomains/thunks';
import thirdLevelDomainValidator from '@utils/validators/thirdLevelDomainValidator';

import ReusableButton from '@common/Button/Button';
import Loading from '@common/Loading/Loading';
import Input from '@components/Auth/Common/Input';
import ValidationResult from '@components/Admin/WildcardDomains/components/ValidationResult';
import CustomTenantDomain from '@components/Admin/WildcardDomains/components/CustomTenantDomain';
import useStyles from './styles';
import DNSTable from './components/DNSTable';

const allowedDomains = ['com', 'org', 'net', 'edu', 'gov', 'info', 'biz'];

const WildcardDomains = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { customDomainVerificationId, defaultHostName } = useSelector(
    resourceSelector,
  );
  const resourceInfoLoading = useSelector(resourceLoadingSelector);
  const validationResult = useSelector(validationResultSelector);
  const validationLoading = useSelector(validationLoadingSelector);
  const assignLoading = useSelector(assignDomainLoadingSelector);
  const assignResult = useSelector(assignResultSelector);
  const customTenantDomain = useSelector(customDomainSelector);

  const [domain, setDomain] = useState('');

  const onMount = useCallback(() => {
    dispatch(ThunkGetCustomDomain());
    dispatch(ThunkGetAzureResourceData());
  }, [dispatch]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  const submit = async values => {
    await dispatch(ThunkAssignDomain(values));
    await dispatch(ThunkGetCustomDomain());
  };

  const onChange = () => {
    setDomain('');
    dispatch(ThunkValidateDomain.rejected());
  };

  const validate = values => ({
    customDomain: thirdLevelDomainValidator(
      values.customDomain,
      allowedDomains,
    ),
  });

  if (resourceInfoLoading || !customTenantDomain) return <Loading />;

  if (assignResult || customTenantDomain.domain)
    return (
      <Paper className={classes.container}>
        <CustomTenantDomain customTenantDomain={customTenantDomain} />
      </Paper>
    );

  return (
    <Paper className={classes.container}>
      <Alert color="info" severity="info" className={classes.alert}>
        <strong>Welcome to the Custom Domains feature!</strong>
        <br />
        You can customize what URL your customers will be see when you send them
        an Opt-in Form, an End-User Verification link (coming soon!), or a
        Secure Data link (also coming soon).
      </Alert>

      <Paper className={`${classes.container} ${classes.stepContainer}`}>
        <Typography variant="h6" className={classes.stepTitle}>
          Step 1: Choose a Custom Domain
        </Typography>
        <Box className={classes.formContainer}>
          <Form
            onSubmit={submit}
            validate={validate}
            initialValues={{ customDomain: '' }}
            render={({ handleSubmit, values, invalid }) => (
              <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <Field
                  id="customDomain"
                  label="Custom Domain"
                  name="customDomain"
                  styleType="main"
                  fullWidth
                  component={Input}
                  disabled={assignLoading || !!customTenantDomain.domain}
                  onInput={() => {
                    onChange();
                  }}
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!invalid) {
                        setDomain(values.customDomain);
                      }
                    }
                  }}
                />

                <div className={classes.buttonGroup}>
                  <ReusableButton
                    size="md"
                    type="button"
                    viewType="blue"
                    disabled={
                      !values.customDomain || validationLoading || assignLoading
                    }
                    label="Submit"
                    onClick={() =>
                      invalid ? undefined : setDomain(values.customDomain)
                    }
                  />
                </div>
              </form>
            )}
          />
        </Box>
      </Paper>
      {domain && (
        <>
          <Paper className={`${classes.container} ${classes.stepContainer}`}>
            <Typography variant="h6" className={classes.stepTitle}>
              Step 2: Configure DNS Records
            </Typography>
            <Typography>
              Create a TXT record and a CNAME record using the information
              below. When that&apos;s done, proceed to Step 3.
            </Typography>
            <DNSTable
              records={[
                {
                  type: 'TXT',
                  name: `asuid.${domain
                    .split('.')
                    .slice(0, -2)
                    .join('.')}`,
                  content: customDomainVerificationId,
                },
                {
                  type: 'CNAME',
                  name: domain
                    .split('.')
                    .slice(0, -2)
                    .join('.'),
                  content: defaultHostName,
                },
              ]}
            />
          </Paper>
          <Paper className={`${classes.container} ${classes.stepContainer}`}>
            <Typography variant="h6" className={classes.stepTitle}>
              Step 3: Verify and Save Your Custom Domain
            </Typography>

            <div className={classes.infoSection}>
              <Typography>
                Click the Validate button to confirm that everything has been
                setup correctly. Click the Save button to save your settings.
              </Typography>
              {validationResult && (
                <div className={classes.validationResult}>
                  <ValidationResult data={validationResult} />
                </div>
              )}
              <div className={classes.buttonGroup}>
                <div className={classes.buttonContainer}>
                  <ReusableButton
                    size="md"
                    viewType="blue"
                    type="button"
                    label="Validate"
                    onClick={() => {
                      dispatch(ThunkValidateDomain(domain));
                    }}
                  />
                  <ReusableButton
                    size="md"
                    viewType="blue"
                    type="button"
                    label="Save"
                    disabled={!validationResult?.isSuccess || assignLoading}
                    onClick={() => {
                      submit(domain);
                    }}
                  />
                </div>
              </div>

              {(validationLoading || assignLoading) && (
                <div className={classes.progressContainer}>
                  <LinearProgress />
                </div>
              )}
            </div>
          </Paper>
        </>
      )}
    </Paper>
  );
};

export default WildcardDomains;
